import { Component, inject, input, output, signal, OnInit, computed, DestroyRef, effect, untracked } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ProfileContextService } from '@/core/auth/services/profile-context';
import { FieldDef, FormSchema, OptionDto } from '../types';
import { buildFormGroup } from '../build-form';
import { resolveFormSchemaForProfile } from '../resolve-form-schema';
import { CommonModule } from '@angular/common';
import { DynamicField } from '../dynamic-field/dynamic-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { sanitizeForApi } from '@/shared/data/sanitize';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FkStore } from '@/shared/store/common/fk-store';
import { dateToIsoString } from '@/shared/utils/date/timezone';

@Component({
  selector: 'app-dynamic-form',
  imports: [
    DynamicField,
    CommonModule, ReactiveFormsModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './dynamic-form.html'
})
export class DynamicForm<T> implements OnInit {

  private fb = inject(NonNullableFormBuilder);
  private destroyRef = inject(DestroyRef);
  private fkStore = inject(FkStore);
  private profileContext = inject(ProfileContextService);

  schema = input.required<FormSchema<T>>();
  initial = input<Partial<T>>();
  readonly = input(false);
  compact = input(false);
  layout = input<'grid' | 'kv'>('grid');
  scrollSpacer = input(true); // Controls extra bottom space for sticky footers

  /** Profile-resolved effective schema — applies showOnProfiles, hideOnProfiles, profileOverrides, orderOnProfiles */
  protected effectiveSchema = computed(() => resolveFormSchemaForProfile(this.schema(), this.profileContext.profile()));

  formChange = output<T>();
  formValid = output<boolean>();
  refLabelSelected = output<{ key: string; id: number | string; label: string; option?: OptionDto }>();

  form!: FormGroup;
  formReady = signal(false);
  formValueJson = signal('{}');
  protected formValueSignal = signal<Partial<T>>({});

  // Sincronizar el formulario cuando cambia el input initial (evita parpadeos de UI)
  private _syncEffect = effect(() => {
    const init = this.initial();
    const hasData = init && Object.keys(init).length > 0;

    if (this.formReady() && hasData) {
      untracked(() => {
        const currentVal = this.form.getRawValue();
        const controlsChanged = Object.keys(this.form.controls).some(key =>
          Object.prototype.hasOwnProperty.call(init, key) &&
          JSON.stringify(init[key as keyof T]) !== JSON.stringify(currentVal[key]),
        );
        const nextValue = this.mergeLabelFields(currentVal as Partial<T>, init);
        const labelsChanged = JSON.stringify(nextValue) !== JSON.stringify(this.formValueSignal());

        if (controlsChanged) {
          // Si es diferente, emitimos evento para que los controles reaccionen (ej: currency field)
          this.form.reset(init, { emitEvent: false });
          this.form.markAsPristine();

          const newVal = this.mergeLabelFields(this.form.getRawValue() as Partial<T>, init);
          this.formValueSignal.set(newVal);

          this.syncHiddenFieldStates(newVal as Partial<T>);

          this.formValid.emit(this.form.valid);
          this.updateFormValueJson();
        } else if (labelsChanged) {
          this.formValueSignal.set(nextValue);
          this.syncHiddenFieldStates(nextValue);
          this.updateFormValueJson();
        }
      });
    } else {
      // console.log('[DynamicForm] Skipping sync. Ready:', this.formReady(), 'HasData:', hasData);
    }
  });

  protected viewFields = computed(() => {
    const fields = this.effectiveSchema();
    const formValue = this.formValueSignal();
    let lastSection: string | null = null;

    return fields
      .filter(f => !f.hideInForm)
      .filter(f => !f.hidden || !f.hidden(formValue))
      .map(f => {
        const isNew = f.section && f.section !== lastSection;
        if (f.section) lastSection = f.section;
        return { ...f, isNewSection: !!isNew };
      });
  });

  ngOnInit() {
    this.form = buildFormGroup<T>(this.fb, this.effectiveSchema(), this.initial());

    this.formReady.set(true);
    this.formValid.emit(this.form.valid);

    const initialValue = this.initial();
    const isEdit = initialValue && 'id' in initialValue && !!initialValue.id;

    if (!this.readonly() && isEdit) {
      this.form.markAllAsTouched();
    }

    const initialRawValue = this.form.getRawValue() as Partial<T>;
    this.formValueSignal.set(this.mergeLabelFields(initialRawValue, this.initial()));
    this.syncHiddenFieldStates(initialRawValue);
    this.updateFormValueJson();
    this.formValid.emit(this.form.valid);
    if (!this.readonly() && initialValue && Object.values(initialValue).some(value => value != null && value !== '')) {
      let model = this.formValueSignal();
      for (const field of this.effectiveSchema()) {
        const value = model[field.key];
        if (field.type === 'date' && value instanceof Date) {
          model = { ...model, [field.key]: dateToIsoString(value) };
        }
      }
      this.formChange.emit(model as T);
    }

    // Sincronizar estados de campos ocultos
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const rawValue = this.form.getRawValue() as Partial<T>;
        const mergedValue = this.mergeLabelFields(rawValue, this.formValueSignal());
        this.formChange.emit(mergedValue as T);

        this.syncHiddenFieldStates(rawValue);

        this.formValid.emit(this.form.valid);

        queueMicrotask(() => {
          this.formValueSignal.set(mergedValue);
          this.updateFormValueJson();
        });
      });

    this.form.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formValid.emit(this.form.valid);
      });
  }

  refOf(f: FieldDef<T>): string {
    const init = this.initial();
    if (!init) return '-';

    // 1. Try cell function (highest priority for custom formatting)
    if (f.cell) return f.cell(init);

    // 2. Try label field from data (already present in DTO)
    if (f.labelField) {
      const v = init[f.labelField];
      if (v != null && v !== '') return String(v);
    }

    // 3. Fallback to FK Store resolution if we have an ID (resolves naming discrepancies)
    if (f.type === 'ref' && f.refTable) {
      const id = init[f.key as keyof T] as number | string | null;
      if (id != null) {
        const table = typeof f.refTable === 'function' ? f.refTable(init) : f.refTable;
        const label = this.fkStore.getLabelSync(table, id);
        // If label found and it's not just the ID echoed back, use it
        if (label && label !== String(id)) return label;
      }
    }

    return '-';
  }

  protected onRefLabelSelected(
    field: FieldDef<T>,
    event: { key: string; id: number | string; label: string; option?: OptionDto },
  ): void {
    this.refLabelSelected.emit(event);
    if (!field.labelField) return;

    const labelPatch = { [field.labelField]: event.label } as Partial<T>;
    const nextValue = {
      ...this.formValueSignal(),
      ...this.form.getRawValue(),
      ...labelPatch,
    } as T;

    this.formValueSignal.set(nextValue);
    this.formChange.emit(nextValue);
    this.updateFormValueJson();
  }

  private updateFormValueJson(): void {
    this.formValueJson.set(JSON.stringify(this.formValueSignal(), null, 2));
  }

  getFormValue = (): T => sanitizeForApi({
    ...this.formValueSignal(),
    ...this.form.getRawValue(),
  }) as T;
  validateForm = (): boolean => (this.form.markAllAsTouched(), this.form.valid);

  spanClass(f: FieldDef<T>) {
    const span = f.colSpan ? `col-span-${f.colSpan}` : '';
    const extra = f.type === 'textarea' && !f.colSpan ? 'col-span-4' : '';
    return [span, extra].filter(Boolean).join(' ');
  }

  private syncHiddenFieldStates(formValue: Partial<T>): void {
    this.effectiveSchema().forEach(field => {
      if (!field.hidden) return;

      const ctrl = this.form.get(field.key);
      if (!ctrl) return;

      const isHidden = field.hidden(formValue);
      const isReadonly = typeof field.readonly === 'function' ? field.readonly(formValue) : !!field.readonly;

      if (isHidden && ctrl.enabled) {
        ctrl.disable({ emitEvent: false });
      }

      if (!isHidden && ctrl.disabled && !this.readonly() && !isReadonly) {
        ctrl.enable({ emitEvent: false });
      }
    });
  }

  private mergeLabelFields(base: Partial<T>, source?: Partial<T>): Partial<T> {
    if (!source) return base;

    const next = { ...base };
    for (const field of this.effectiveSchema()) {
      if (!field.labelField) continue;
      const label = source[field.labelField];
      if (label != null && label !== '') {
        next[field.labelField] = label;
      }
    }

    return next;
  }

}
